/**
 * Brush Type System
 * Polymorphic paint types for fills, strokes, and backgrounds
 */

// Gradient stop definition
export interface GradientStop {
  color: string;    // Hex color
  position: number; // 0-100
}

// Solid color brush
export interface SolidBrush {
  type: 'solid';
  color: string;  // Hex color, e.g. '#FFFFFF'
}

// Linear gradient brush
export interface LinearGradientBrush {
  type: 'linear-gradient';
  stops: GradientStop[];
  angle: number;  // Degrees
}

// Radial gradient brush
export interface RadialGradientBrush {
  type: 'radial-gradient';
  stops: GradientStop[];
  centerX: number;  // 0-100
  centerY: number;  // 0-100
}

// Sweep (conic) gradient brush
export interface SweepGradientBrush {
  type: 'sweep-gradient';
  stops: GradientStop[];
  centerX: number;  // 0-100
  centerY: number;  // 0-100
  startAngle: number;  // Degrees, where the gradient starts
}

// Transparent / no fill
export interface TransparentBrush {
  type: 'transparent';
}

// Union type for all brush types
export type Brush = SolidBrush | LinearGradientBrush | RadialGradientBrush | SweepGradientBrush | TransparentBrush;

// Canvas backgrounds can also be images
export interface ImageBrush {
  type: 'image';
  src: string;
  objectFit?: 'cover' | 'contain' | 'fill';
}

// Extended brush type for backgrounds (includes image option)
export type BackgroundBrush = Brush | ImageBrush;

// ============================================
// Helper functions
// ============================================

/**
 * Create a solid color brush
 */
export function solid(color: string): SolidBrush {
  return { type: 'solid', color };
}

/**
 * Create a linear gradient brush
 */
export function linearGradient(
  stops: GradientStop[],
  angle: number = 180
): LinearGradientBrush {
  return { type: 'linear-gradient', stops, angle };
}

/**
 * Create a radial gradient brush
 */
export function radialGradient(
  stops: GradientStop[],
  centerX: number = 50,
  centerY: number = 50
): RadialGradientBrush {
  return { type: 'radial-gradient', stops, centerX, centerY };
}

/**
 * Create a sweep (conic) gradient brush
 */
export function sweepGradient(
  stops: GradientStop[],
  centerX: number = 50,
  centerY: number = 50,
  startAngle: number = 0
): SweepGradientBrush {
  return { type: 'sweep-gradient', stops, centerX, centerY, startAngle };
}

/**
 * Create a transparent brush
 */
export function transparent(): TransparentBrush {
  return { type: 'transparent' };
}

/**
 * Create an image brush (for backgrounds)
 */
export function imageBrush(
  src: string,
  objectFit: 'cover' | 'contain' | 'fill' = 'cover'
): ImageBrush {
  return { type: 'image', src, objectFit };
}

// ============================================
// Conversion utilities
// ============================================

/**
 * Convert a brush to CSS value
 */
export function brushToCss(brush: Brush | BackgroundBrush): string {
  switch (brush.type) {
    case 'solid':
      return brush.color;

    case 'transparent':
      return 'transparent';

    case 'linear-gradient': {
      const stops = brush.stops
        .map(s => `${s.color} ${s.position}%`)
        .join(', ');
      return `linear-gradient(${brush.angle}deg, ${stops})`;
    }

    case 'radial-gradient': {
      const stops = brush.stops
        .map(s => `${s.color} ${s.position}%`)
        .join(', ');
      return `radial-gradient(ellipse at ${brush.centerX}% ${brush.centerY}%, ${stops})`;
    }

    case 'sweep-gradient': {
      const stops = brush.stops
        .map(s => `${s.color} ${s.position}%`)
        .join(', ');
      return `conic-gradient(from ${brush.startAngle}deg at ${brush.centerX}% ${brush.centerY}%, ${stops})`;
    }

    case 'image':
      return `url(${brush.src})`;

    default:
      return 'transparent';
  }
}

/**
 * Check if a brush is effectively transparent
 */
export function isTransparent(brush: Brush | BackgroundBrush | undefined): boolean {
  if (!brush) return true;
  if (brush.type === 'transparent') return true;
  if (brush.type === 'solid' && brush.color === 'transparent') return true;
  return false;
}

/**
 * Check if a brush is a gradient
 */
export function isGradient(brush: Brush | BackgroundBrush): brush is LinearGradientBrush | RadialGradientBrush | SweepGradientBrush {
  return brush.type === 'linear-gradient' || brush.type === 'radial-gradient' || brush.type === 'sweep-gradient';
}

/**
 * Get the primary color from a brush (for fallbacks)
 */
export function getPrimaryColor(brush: Brush | BackgroundBrush): string {
  switch (brush.type) {
    case 'solid':
      return brush.color;
    case 'linear-gradient':
    case 'radial-gradient':
    case 'sweep-gradient':
      return brush.stops[0]?.color || '#000000';
    case 'transparent':
      return 'transparent';
    case 'image':
      return '#000000';
    default:
      return '#000000';
  }
}

/**
 * Convert legacy string color to Brush
 * Used for migrating old formats
 */
export function stringToBrush(value: string | undefined): Brush {
  if (!value || value === 'transparent' || value === 'none') {
    return { type: 'transparent' };
  }
  return { type: 'solid', color: value };
}

// ============================================
// Default brushes
// ============================================

export const DEFAULT_FILL: Brush = { type: 'solid', color: '#FFFFFF' };
export const DEFAULT_STROKE: Brush = { type: 'solid', color: '#000000' };
export const DEFAULT_TEXT_COLOR: Brush = { type: 'solid', color: '#FFFFFF' };
export const DEFAULT_BACKGROUND: BackgroundBrush = { type: 'solid', color: '#0A1628' };
export const NO_FILL: Brush = { type: 'transparent' };
export const NO_STROKE: Brush = { type: 'transparent' };
